import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrayFact,
  EntitySchema,
  EntityCreationFact,
  EntityFieldValue,
  FactConflict,
  FactValue,
  ScalarFact,
  isArrayFact,
  factDisplayName,
  numberlikeFields,
} from "../types";
import { isCellValueEmpty } from "../../Search/TablePanel/cellEmptiness";
import useSearchable from "../Components/Searchable";
import EditableFactRenderer from "../Components/EditableFactRenderer";
import { BulkAutofillToolbarAction } from "../Components/AutofillTriggers";
import FactsGrid from "./FactsGrid";
import { isDerivedFact } from "../Overview/previewData";
import ErrorRenderer from "../Components/ErrorRenderer";
import { useSaveEntityFacts } from "../Components/useSaveEntityFacts";
import { dedupeSetFact, dedupeSetFacts } from "./setFactDedupe";
import { useCanEditRms } from "@/hooks/useCanEditRms";
import { useEntitySchema } from "../../Data";
import { create } from "zustand";

// Symbol to represent fact deletion in draft state
export const DELETED_FACT = Symbol("DELETED_FACT");

// The baseline a drafted fact was captured against — the server value
// the user saw when they started editing. Sent back to the PATCH
// handler as expected_value / expected_values so the backend can
// reject the save with 409 if somebody else (or AI autofill) wrote a
// different value in between. One is_array flag pins which of the two
// shapes the baseline takes, matching the server payload (issue #4914).
export interface FactBaseline {
  isArray: boolean;
  value?: EntityFieldValue;
  values?: EntityFieldValue[];
  // Encoded server value at baseline-capture time. Pinned into the
  // render key while a draft is active so a server-value refresh
  // (autofill landing, another user saving) doesn't remount the editor
  // and wipe the user's in-progress edit. Flips to the fresh
  // serverValueKey on clearDraft, which is when we WANT the remount.
  key: string;
}

const buildBaselineFromFact = (fact: ScalarFact | ArrayFact): FactBaseline => {
  if (isArrayFact(fact)) {
    return {
      isArray: true,
      // OCC expected_values are the server's stored value, duplicates
      // included — a baseline built from the de-duped array would 409 every
      // save of exactly the records the set-dedupe rule exists for.
      values: fact.array.map((v) => ({
        scalar: v.scalar,
        entity: v.entity?.id ?? null,
      })),
      // The KEY is the rendered row's identity, and the row renders the
      // de-duped fact. Keyed on the pristine value instead, the first dirty
      // edit of a duplicated fact flips the row's render key and remounts
      // the editor mid-typing, discarding the visible draft.
      key: serverValueKeyFor(dedupeSetFact(fact)),
    };
  }
  return {
    isArray: false,
    value: {
      scalar: fact.scalar,
      entity: fact.entity?.id ?? null,
      ...numberlikeFields(fact),
    },
    key: serverValueKeyFor(fact),
  };
};

// Render key for an editable fact. Falls back to the server-value key
// for pristine rows (so autofill writes surface as a remount), but
// freezes to the captured baseline key once the user starts editing
// so their local draft survives any concurrent server write.
// Reads baselineFacts via getState() rather than subscribing — FactsTab
// subscribing to the baseline map would re-render on every setDraft
// call and cascade into an infinite loop via ScalarValueEditor's
// onChange-identity-keyed useEffect.
const factRendererKey = (fact: ScalarFact | ArrayFact): string => {
  const baseline = useEntityDraftStore.getState().baselineFacts[fact.name];
  return `${fact.name}::${baseline?.key ?? serverValueKeyFor(fact)}`;
};

// Whether a fact holds anything a user would lose sight of if its row vanished.
// Drives the one asymmetry in conditional facts (#10968): a not-applicable fact
// is dropped only when it is *empty*. A populated one keeps its row, because the
// org's rules describe relevance, not permission to hide existing data.
//
// Delegates to the shared `isCellValueEmpty` rather than re-deriving emptiness:
// that one also treats whitespace-only strings and the empty-address shell
// (`{formatted: ""}`) as empty, so a hidden fact holding blank text is dropped
// instead of rendering a "not applicable" note around an empty-looking editor.
const isPopulated = (fact: ScalarFact | ArrayFact): boolean =>
  !isCellValueEmpty(
    isArrayFact(fact)
      ? fact.array
      : { scalar: fact.scalar, entity: fact.entity }
  );

const UNGROUPED_KEY = "__ungrouped__";

// Encode a fact's current server value into a stable key. Includes the
// fact's queryId so an autofill that re-writes the same scalar still
// changes the key — every autofill run gets a fresh query_id, so the
// editor remounts and surfaces the (re)filled value.
const serverValueKeyFor = (fact: ScalarFact | ArrayFact): string =>
  isArrayFact(fact)
    ? JSON.stringify(
        fact.array.map((v) => ({
          s: v.scalar,
          e: v.entity?.id ?? null,
          q: v.queryId ?? null,
        }))
      )
    : JSON.stringify({
        s: fact.scalar,
        e: fact.entity?.id ?? null,
        q: fact.queryId ?? null,
      });

interface FactsTabProps {
  entity: EntitySchema;
  title?: string;
}

const FactsTab: React.FC<FactsTabProps> = ({ entity, title = "Facts" }) => {
  // Subscribed, not read once: a row has to reopen the moment a save comes back
  // 409, without the user touching anything.
  //
  // This is the subscription the warnings below (and on `useFactChangeAggregator`)
  // say to avoid, and it is safe only because three memos hold identity across a
  // draft-only change: `useSearchable`'s `filteredItems` -> the `facts` memo ->
  // `handlersByFactName`. FactsTab now re-renders on every keystroke, so if any
  // one of those stops memoizing, the documented onChange-identity loop comes
  // back on the hot path rather than in some rare corner.
  const draftFacts = useEntityDraftStore((state) => state.draftFacts);
  const draftDisplay = useEntityDraftStore((state) => state.draftDisplay);
  const conflictFacts = useEntityDraftStore((state) => state.conflictFacts);
  const canEditRms = useCanEditRms();

  // Closing a row is the save (#11880). The grid says when the user has left
  // the rows they were editing; this writes whatever they drafted. While the
  // request is out the row already READS the value it is saving — an editor
  // left standing for the round-trip re-formats itself on blur and reads as
  // a glitch — and a rejection reopens it with the draft intact.
  const {
    save,
    withdraw,
    flush,
    savingFacts,
    error: saveError,
    clearError: clearSaveError,
  } = useSaveEntityFacts(entity.id, entity.kind, entity.name);

  // The fact as it will read once the save in flight lands, or null for a
  // row with nothing on the wire.
  const savingView = useCallback(
    (fact: ScalarFact | ArrayFact): ScalarFact | ArrayFact | null => {
      if (!savingFacts.has(fact.name)) return null;
      const display = draftDisplay[fact.name];
      if (display === undefined) return null;
      if (isArrayFact(fact)) {
        return Array.isArray(display) ? { ...fact, array: display } : null;
      }
      if (Array.isArray(display)) return null;
      // The user's own value carries none of the stored value's provenance:
      // the AI mark, its answer doorway and the review badge belong to what
      // it is replacing, and the server clears them on write anyway.
      return {
        ...fact,
        scalar: display.scalar,
        entity: display.entity,
        queryId: null,
        workspaceId: null,
        staging: false,
      };
    },
    [savingFacts, draftDisplay]
  );

  // Leaving the tab — for another tab, another record, or by browser Back —
  // is closing the rows too. Nothing was clicked, so the grid never sees it;
  // the drafts are written here, before the change aggregator below clears
  // the store for the next record (its cleanup runs after this one). The
  // request outlives this component; the hook checks the store still holds
  // this record before it touches any draft. A failure here has nowhere to
  // show: the tab is gone, and so is the draft.
  useEffect(() => () => flush(), [entity.id, flush]);

  // The tab states each set member once (#11426): a duplicated hierarchy
  // path renders — and edits — as one row. Everything downstream (rows,
  // counts, editor seeds) reads this list; the change aggregator deliberately
  // does NOT, so OCC baselines keep the pristine stored values.
  const dedupedFacts = useMemo(
    () => dedupeSetFacts(entity.facts),
    [entity.facts]
  );

  const {
    searchTerm,
    items: matchedFacts,
    SearchBar,
  } = useSearchable(dedupedFacts, (term, fact) => {
    // Match the rendered label as well as the canonical name -- otherwise a
    // relabelled fact can't be found by the name it's showing the user
    // (#10968).
    const lower = term.toLowerCase();
    return (
      factDisplayName(fact).toLowerCase().includes(lower) ||
      fact.name.toLowerCase().includes(lower)
    );
  });

  // Facts this record actually renders. A fact the org's conditional rules mark
  // not applicable is dropped only when it is empty; a populated one stays so
  // its value remains visible and editable (#10968). Everything downstream --
  // counts, grouping, the empty state -- reads from this so the numbers always
  // match what is on screen.
  // What each of this record type's attributes MEANS to the system (#11270).
  // The entity payload carries values, not attribute metadata, so the
  // designations are read off this kind's schema -- already in the shared RMS
  // bootstrap cache, so this costs no request -- and merged onto the facts
  // below. `name` is the identity key both sides agree on.
  const { data: schema } = useEntitySchema(entity.kind);
  const designationByFactName = useMemo(
    () =>
      new Map(
        (schema?.facts ?? [])
          .filter((f) => f.designates)
          .map((f) => [f.name, f.designates])
      ),
    [schema]
  );

  const facts = useMemo(
    () =>
      matchedFacts
        .filter((fact) => !fact.hidden || isPopulated(fact))
        .map((fact) => {
          const designates = designationByFactName.get(fact.name);
          // Cloning only the designated ones keeps object identity stable for
          // every other row, which is what the render key and the draft store
          // are keyed against.
          return designates ? { ...fact, designates } : fact;
        }),
    [matchedFacts, designationByFactName]
  );

  // The fact whose value drove those rules, for the "why" note on a kept row.
  const controller = entity.conditional_controller ?? null;

  const handlersByFactName = useFactChangeAggregator(
    entity.id,
    entity.kind,
    entity.facts
  );
  const groups = entity.groups ?? [];
  const hasGroups = groups.length > 0;

  // Bucket facts by group_id in the group's order (groups sorted by
  // position); ungrouped trails last.
  //
  // A group with nothing left to show is dropped rather than rendered as an
  // empty box (#11145). Two things empty a group -- a search that matched none
  // of its facts, and the org's conditional rules marking all of them not
  // applicable (#10968) -- and hiding it is right for both. It is safe because
  // an attribute with no *value* is not an absent fact: it still arrives here
  // and still renders as a blank editable row, so a dropped group never hides a
  // field the user could have filled in. This is what `Ungrouped` below has
  // always done; named groups were the inconsistency.
  // The derived facts (Date Created, Last Interaction, ..., #14680) are
  // ungrouped facts like any attribute nobody put in a group: the record
  // detail serves them with no group, so they read in Ungrouped. The grid
  // lists them after the section's own attributes and leaves them out of
  // every count.
  const bucketedFacts = useMemo(() => {
    if (!hasGroups) return null;
    const byGroup = new Map<string, typeof facts>();
    for (const fact of facts) {
      const key = fact.group_id == null ? UNGROUPED_KEY : String(fact.group_id);
      const arr = byGroup.get(key) ?? [];
      arr.push(fact);
      byGroup.set(key, arr);
    }
    const orderedGroups = [...groups].sort(
      (a, b) => a.position - b.position || a.name.localeCompare(b.name)
    );
    const sections: {
      key: string;
      label: string;
      facts: typeof facts;
    }[] = [];
    for (const g of orderedGroups) {
      const groupFacts = byGroup.get(String(g.id));
      if (!groupFacts || groupFacts.length === 0) continue;
      sections.push({
        key: String(g.id),
        label: g.name,
        facts: groupFacts,
      });
    }
    const ungrouped = byGroup.get(UNGROUPED_KEY);
    if (ungrouped && ungrouped.length > 0) {
      sections.push({
        key: UNGROUPED_KEY,
        label: "Ungrouped",
        facts: ungrouped,
      });
    }
    return sections;
  }, [facts, groups, hasGroups]);

  // The empty state. A search's is that nothing matched, derived facts
  // included. At rest the record's own attributes decide it: a derived fact
  // is defined on every record, so a record type with no attributes of its
  // own still holds nothing (#14680).
  const showsEmptyState = useMemo(
    () => (searchTerm ? facts.length === 0 : facts.every(isDerivedFact)),
    [searchTerm, facts]
  );

  // Multi-select for the bulk "autofill selected" toolbar action.
  // Stored as attribute ids so it survives reorder and re-bucketing.
  const [selectedAttributeIds, setSelectedAttributeIds] = useState<Set<number>>(
    () => new Set()
  );

  // Selected AI-enabled attribute ids for the bulk toolbar action. We
  // recompute from the set to drop any ids that are no longer AI-enabled
  // (e.g., a schema edit flipped ai_autofill off while selections were
  // live). Only these get sent to the bulk endpoint.
  const selectedAIAttributes = useMemo<
    { id: number; lastOutcomeAt: string | null }[]
  >(() => {
    const result: { id: number; lastOutcomeAt: string | null }[] = [];
    for (const fact of entity.facts) {
      if (!fact.ai_autofill) continue;
      const numericId = Number(fact.id);
      if (Number.isFinite(numericId) && selectedAttributeIds.has(numericId)) {
        result.push({
          id: numericId,
          lastOutcomeAt: fact.autofill_last_outcome_at ?? null,
        });
      }
    }
    return result;
  }, [entity.facts, selectedAttributeIds]);

  const clearSelection = () => setSelectedAttributeIds(new Set());

  // One fact row. A row kept despite being not applicable is wrapped in a muted,
  // dashed container with a note naming the fact that made it irrelevant -- it
  // stays fully interactive, so the annotation informs without blocking (#10968).
  const renderFact = useCallback(
    (fact: ScalarFact | ArrayFact) => {
      const row = (
        <EditableFactRenderer
          definition={fact}
          fact={fact}
          // A row reopened while its draft is on the wire, or after the
          // server rejected it, opens on the draft rather than the stored
          // value (#11880). Read at render rather than subscribed, like the
          // render key above: the editor only looks at it when it mounts,
          // and keying this callback on the draft map would re-mint it on
          // every keystroke.
          draft={useEntityDraftStore.getState().draftDisplay[fact.name]}
          onChange={handlersByFactName.get(fact.name)}
          currentEntityId={entity.id}
          // The open editor keeps the read rows' key column so the row
          // doesn't jump (#11426 nooks N6).
          layout="grid"
          // Grouped facts sit inside an `overflow-hidden` box, which clips an
          // in-flow dropdown at the group's bottom edge. Portalling lifts the
          // entity picker's list out of it so the whole list stays reachable.
          portalDropdown
          autofillContext={
            fact.ai_autofill
              ? {
                  publicEntityId: entity.id,
                  attributeId: Number(fact.id),
                  lastOutcomeAt: fact.autofill_last_outcome_at ?? null,
                }
              : undefined
          }
        />
      );
      // `hidden` without a controller shouldn't happen -- the server only sets
      // it when a rule matched -- so degrade to the plain row rather than
      // showing a note that can't name a reason.
      if (!fact.hidden || controller === null) {
        return (
          <React.Fragment key={factRendererKey(fact)}>{row}</React.Fragment>
        );
      }
      return (
        <div
          key={factRendererKey(fact)}
          className="rounded-[10px] border border-dashed border-gray-200 bg-gray-50/60"
        >
          {/* The note itself is the grid's to render (`notApplicableNote`), so
              this wrapper contributes the dashed treatment only. Both used to
              print it and the row explained itself twice in edit mode. */}
          {row}
        </div>
      );
    },
    [entity.id, handlersByFactName, controller]
  );

  return (
    <div>
      <div className="pb-4">
        {/* Capped: a full-column search field was the widest control in the
            RMS, for a filter over a label list (round 8). */}
        <div style={{ maxWidth: 320 }}>{SearchBar}</div>

        {selectedAIAttributes.length > 0 && (
          <div className="mt-3 flex items-center gap-3 px-3 py-2 rounded-md bg-[#F7F8FF] text-[#2A3DCB]">
            <span className="text-sm">
              {selectedAIAttributes.length} selected
            </span>
            <BulkAutofillToolbarAction
              publicEntityId={entity.id}
              attributes={selectedAIAttributes}
              onDispatched={clearSelection}
            />
            <button
              type="button"
              onClick={clearSelection}
              className="ml-auto text-xs text-[#2A3DCB] hover:underline"
            >
              Clear selection
            </button>
          </div>
        )}
      </div>

      {/* A save the server refused for a reason other than a conflict. The
          drafts stay in their rows (still open, still editable); the next
          close tries again. Conflicts paint the field itself instead. */}
      {saveError ? (
        <div className="pb-4">
          <ErrorRenderer error={saveError} onDismiss={clearSaveError} />
        </div>
      ) : null}

      {/* The PAGE owns the scroll (round 8): cards on the ground don't nest
          scrollers in this system — only the grid shell does. */}
      <div>
        {/* Said above the derived facts when there are any (#14680). */}
        {showsEmptyState ? (
          <p style={{ color: "var(--fp-ink-3)" }}>
            {searchTerm ? "No facts match your search." : "No facts held."}
          </p>
        ) : null}
        {facts.length === 0 ? null : (
          <FactsGrid
            entity={entity}
            /* Which rows are open belongs to the record you opened them on.
               The route does not remount on a param change, so without this
               key, navigating from one record to another carried the open set
               across and the new record opened a row nobody asked for. */
            key={entity.id}
            sections={
              bucketedFacts
                ? bucketedFacts.map((section) => ({
                    key: section.key,
                    label: section.label,
                    facts: section.facts,
                  }))
                : [{ key: "all", label: title, facts }]
            }
            renderEditor={renderFact}
            notApplicableNote={(fact) =>
              fact.hidden && controller
                ? `Not applicable when ${controller.name} is ${controller.value}`
                : null
            }
            mustStayOpen={(fact) =>
              fact.name in draftFacts || fact.name in conflictFacts
            }
            onClose={save}
            onOpen={withdraw}
            savingView={savingView}
            canEdit={canEditRms}
            // A search overrides the unfilled fold (round 10): every match
            // shows, filled or not, so the search reaches every field.
            searchActive={Boolean(searchTerm)}
          />
        )}
      </div>
    </div>
  );
};

export default FactsTab;

// Hook to build per-fact onChange handlers whose identity is stable
// across renders as long as the set of facts and their current server
// values don't change. Keeping identity stable matters because
// ScalarValueEditor has a useEffect keyed on the onChange prop — if
// the prop changes on every render, the effect fires in a loop that
// thrashes the draft store (issue #4914).
const useFactChangeAggregator = (
  entityId: string,
  entityKind: string,
  facts: ReadonlyArray<ScalarFact | ArrayFact>
) => {
  const setDraft = useEntityDraftStore((state) => state.setDraft);
  const clearDraft = useEntityDraftStore((state) => state.clearDraft);

  // Clear draft when entity changes
  useEffect(() => {
    return () => {
      clearDraft();
    };
  }, [entityId, clearDraft]);

  const handlersByFactName = useMemo(() => {
    const map = new Map<
      string,
      (isDirty: boolean, draftValue: FactValue | FactValue[]) => void
    >();
    for (const fact of facts) {
      // Freeze the baseline at the server value captured when the map
      // was built. setDraft itself is append-only on the baseline
      // (first writer wins), so if the user hasn't edited yet this is
      // the real baseline; if they have, setDraft ignores it in favor
      // of the one already stored.
      const baseline = buildBaselineFromFact(fact);
      map.set(
        fact.name,
        (isDirty: boolean, draftValue: FactValue | FactValue[]) => {
          let factValue: typeof DELETED_FACT | EntityCreationFact | null = null;

          if (isDirty) {
            if (Array.isArray(draftValue)) {
              // Array fact
              factValue = {
                values: draftValue.map((v: FactValue) => ({
                  scalar: v.scalar,
                  entity: v.entity?.id || null,
                })),
              };
            } else {
              // Scalar fact - draftValue is already a FactValue
              if (draftValue.scalar === null && draftValue.entity === null) {
                factValue = DELETED_FACT;
              } else {
                factValue = {
                  value: {
                    scalar: draftValue.scalar,
                    entity: draftValue.entity?.id || null,
                    ...numberlikeFields(draftValue),
                  },
                };
              }
            }
          }

          setDraft(
            entityId,
            entityKind,
            fact.name,
            factValue,
            draftValue,
            isDirty,
            baseline
          );
        }
      );
    }
    return map;
  }, [entityId, entityKind, setDraft, facts]);

  return handlersByFactName;
};

interface EntityDraftState {
  isDirty: boolean;
  entityId: string | null;
  entityKind: string | null;
  draftFacts: Record<string, EntityCreationFact | null | typeof DELETED_FACT>;
  // The same drafts as the editor holds them — entity objects rather than
  // ids — so a row can READ its pending value while the save is in flight
  // (#11880). The wire shape above carries only an entity id, which cannot
  // be rendered as a link chip.
  draftDisplay: Record<string, FactValue | FactValue[]>;
  // Baseline per drafted factName — captured the first time the user
  // starts editing the field. Sent as expected_value/expected_values on
  // save so the backend can detect that another actor (user or autofill)
  // wrote a different value in between (issue #4914).
  baselineFacts: Record<string, FactBaseline>;
  // Fields whose last save attempt came back with a 409 conflict. The
  // editor for the field paints itself red and shows the current DB
  // value. Saving is withheld while any field is marked; the user's next
  // edit of the field clears the marker, and that save carries the
  // refreshed baseline so their draft overwrites the concurrent write.
  conflictFacts: Record<string, FactConflict>;
  // A save came back OK for `sent` — the drafts as they were when the request
  // left. A fact whose draft is still what was sent is done: its draft,
  // baseline and conflict go, and the row reads the server value. A fact the
  // user edited during the round-trip keeps its draft, but its baseline moves
  // to the value just written: the next save is checked against what the
  // server now holds, not against the value from before this one (#11880).
  markFactsSaved: (
    sent: Record<string, EntityCreationFact | null | typeof DELETED_FACT>
  ) => void;
  setDraft: (
    entityId: string,
    entityKind: string,
    factName: string,
    value: EntityCreationFact | null | typeof DELETED_FACT,
    display: FactValue | FactValue[],
    isDirty: boolean,
    baseline: FactBaseline
  ) => void;
  clearDraft: () => void;
  // Drops the draft + baseline + conflict entries for one fact so the
  // EditableFactRenderer's key falls back to the server-value key.
  dropFactDraft: (factName: string) => void;
  setConflicts: (conflicts: FactConflict[]) => void;
  clearConflict: (factName: string) => void;
}

// Whether two drafts of one fact hold the same value. The aggregator mints a
// fresh object per keystroke, so identity says nothing; content does.
const sameDraft = (
  a: EntityCreationFact | null | typeof DELETED_FACT,
  b: EntityCreationFact | null | typeof DELETED_FACT
): boolean => {
  if (a === DELETED_FACT || b === DELETED_FACT) return a === b;
  return JSON.stringify(a) === JSON.stringify(b);
};

// The baseline a just-written value becomes for a draft that outlived its
// save. A deletion leaves the field empty, so the empty value is the baseline.
const baselineFromSent = (
  sentValue: EntityCreationFact | null | typeof DELETED_FACT,
  isArray: boolean,
  key: string
): FactBaseline => {
  if (sentValue === DELETED_FACT || sentValue === null) {
    return isArray
      ? { isArray: true, values: [], key }
      : { isArray: false, value: { scalar: null, entity: null }, key };
  }
  const values = (sentValue as { values?: EntityFieldValue[] }).values;
  if (values !== undefined) {
    return { isArray: true, values, key };
  }
  const value = (sentValue as { value?: EntityFieldValue }).value;
  return {
    isArray: false,
    value: value ?? { scalar: null, entity: null },
    key,
  };
};

export const useEntityDraftStore = create<EntityDraftState>((set) => ({
  isDirty: false,
  entityId: null,
  entityKind: null,
  draftFacts: {},
  draftDisplay: {},
  baselineFacts: {},
  conflictFacts: {},

  markFactsSaved: (sent) => {
    set((state) => {
      const newDraftFacts = { ...state.draftFacts };
      const newDisplay = { ...state.draftDisplay };
      const newBaselines = { ...state.baselineFacts };
      const newConflicts = { ...state.conflictFacts };
      for (const [factName, sentValue] of Object.entries(sent)) {
        if (!(factName in state.draftFacts)) continue;
        if (sameDraft(state.draftFacts[factName], sentValue)) {
          delete newDraftFacts[factName];
          delete newDisplay[factName];
          delete newBaselines[factName];
          delete newConflicts[factName];
          continue;
        }
        const existing = state.baselineFacts[factName];
        newBaselines[factName] = baselineFromSent(
          sentValue,
          existing?.isArray ?? false,
          existing?.key ?? ""
        );
      }
      return {
        ...state,
        isDirty: Object.keys(newDraftFacts).length > 0,
        draftFacts: newDraftFacts,
        draftDisplay: newDisplay,
        baselineFacts: newBaselines,
        conflictFacts: newConflicts,
      };
    });
  },

  setDraft: (
    entityId,
    entityKind,
    factName,
    value,
    display,
    isDirty,
    baseline
  ) => {
    set((state) => {
      // If switching to a different entity, clear the draft
      if (state.entityId && state.entityId !== entityId) {
        // Gated on isDirty like its siblings: a clean report carries a null
        // value, and a null left under a key would go out as a deletion on
        // the next close (#11880).
        return {
          isDirty,
          entityId,
          entityKind,
          draftFacts: isDirty ? { [factName]: value } : {},
          draftDisplay: isDirty ? { [factName]: display } : {},
          baselineFacts: isDirty ? { [factName]: baseline } : {},
        };
      }

      // Decide whether the incoming (value, isDirty) actually changes
      // anything. Zustand's subscribers compare by reference, and
      // ScalarValueEditor fires onChange inside a useEffect keyed on
      // the onChange prop identity — so allocating a fresh draftFacts
      // or baselineFacts object on a no-op call cascades into an
      // infinite render loop (FactsTab re-renders → new handleChange →
      // effect fires → setDraft → ...). Bail out by returning `state`
      // unchanged whenever the update is a no-op. Only allocate new
      // map references when the content actually differs.
      const prevValue = state.draftFacts[factName];
      const draftHasKey = factName in state.draftFacts;
      const baselineHasKey = factName in state.baselineFacts;

      // By content, not identity: an editor that mounts on a carried draft
      // (a row reopened mid-save or after a rejected save, #11880) reports
      // the draft it was given, and that is not an edit.
      const draftChanges =
        value === null
          ? draftHasKey
          : !draftHasKey || !sameDraft(prevValue, value);
      const baselineChanges = value === null ? baselineHasKey : !baselineHasKey;

      // The user edited the field — drop any stale conflict marker for
      // it. Either they just accepted the new baseline by starting
      // again on top of it, or they typed something else; either way
      // the red highlight is no longer meaningful.
      const conflictHasKey = factName in state.conflictFacts;
      const conflictChanges = conflictHasKey && value !== null && draftChanges;

      if (!draftChanges && !baselineChanges && !conflictChanges) {
        return state;
      }

      let newDraftFacts = state.draftFacts;
      let newDisplay = state.draftDisplay;
      if (draftChanges) {
        newDraftFacts = { ...state.draftFacts };
        newDisplay = { ...state.draftDisplay };
        if (value === null) {
          delete newDraftFacts[factName];
          delete newDisplay[factName];
        } else {
          newDraftFacts[factName] = value;
          newDisplay[factName] = display;
        }
      }

      let newBaselines = state.baselineFacts;
      if (baselineChanges) {
        newBaselines = { ...state.baselineFacts };
        if (value === null) {
          delete newBaselines[factName];
        } else {
          newBaselines[factName] = baseline;
        }
      }

      let newConflicts = state.conflictFacts;
      if (conflictChanges) {
        newConflicts = { ...state.conflictFacts };
        delete newConflicts[factName];
      }

      const hasChanges = Object.keys(newDraftFacts).length > 0;

      return {
        isDirty: hasChanges,
        entityId,
        entityKind,
        draftFacts: newDraftFacts as Record<
          string,
          EntityCreationFact | null | typeof DELETED_FACT
        >,
        draftDisplay: newDisplay,
        baselineFacts: newBaselines,
        conflictFacts: newConflicts,
      };
    });
  },

  clearDraft: () => {
    set({
      isDirty: false,
      entityId: null,
      entityKind: null,
      draftFacts: {},
      draftDisplay: {},
      baselineFacts: {},
      conflictFacts: {},
    });
  },

  dropFactDraft: (factName) => {
    set((state) => {
      const draftHasKey = factName in state.draftFacts;
      const baselineHasKey = factName in state.baselineFacts;
      const conflictHasKey = factName in state.conflictFacts;
      if (!draftHasKey && !baselineHasKey && !conflictHasKey) return state;
      const newDraftFacts = { ...state.draftFacts };
      const newDisplay = { ...state.draftDisplay };
      const newBaselines = { ...state.baselineFacts };
      const newConflicts = { ...state.conflictFacts };
      delete newDraftFacts[factName];
      delete newDisplay[factName];
      delete newBaselines[factName];
      delete newConflicts[factName];
      const remainingDirty = Object.keys(newDraftFacts).length > 0;
      return {
        ...state,
        isDirty: remainingDirty,
        draftFacts: newDraftFacts,
        draftDisplay: newDisplay,
        baselineFacts: newBaselines,
        conflictFacts: newConflicts,
      };
    });
  },

  // Record the conflicts surfaced by a 409 save response AND rebaseline
  // each conflicting field to the current DB value so the next Save
  // click overwrites. The editors for these fields subscribe to
  // conflictFacts[factName] and paint themselves red until the user
  // edits the field or the next save clears the entry.
  setConflicts: (conflicts) => {
    set((state) => {
      const newBaselines = { ...state.baselineFacts };
      const newConflicts = { ...state.conflictFacts };
      for (const c of conflicts) {
        newConflicts[c.attribute_name] = c;
        // Rebaseline to current so a subsequent save proceeds (barring
        // yet another concurrent write). Keep the pinned serverValueKey
        // from the original edit so the editor doesn't remount mid-
        // conflict-display and wipe the user's draft.
        const existing = state.baselineFacts[c.attribute_name];
        newBaselines[c.attribute_name] = c.is_array
          ? {
              isArray: true,
              values: (
                c.current as Array<{
                  scalar: any;
                  entity: string | null;
                }>
              ).map((v) => ({ scalar: v.scalar, entity: v.entity })),
              key: existing?.key ?? "",
            }
          : {
              isArray: false,
              value: {
                scalar: (c.current as { scalar: any; entity: string | null })
                  .scalar,
                entity: (c.current as { scalar: any; entity: string | null })
                  .entity,
              },
              key: existing?.key ?? "",
            };
      }
      return {
        ...state,
        baselineFacts: newBaselines,
        conflictFacts: newConflicts,
      };
    });
  },

  clearConflict: (factName) => {
    set((state) => {
      if (!(factName in state.conflictFacts)) return state;
      const next = { ...state.conflictFacts };
      delete next[factName];
      return { ...state, conflictFacts: next };
    });
  },
}));
